import React from 'react';
import { Card, Col, Row, Tooltip, Typography } from 'antd';
import type { BqKpiDay } from '@/types';
import { BqKpiKey, dayLabel, fmtNum, fmtPct, fmtUsd, latestVsAverage } from './bqMath';

const { Text } = Typography;

interface TileDef {
  key: BqKpiKey;
  label: string;
  format: (v: number | null) => string;
  /** Whether a rise is good news (green) or bad news (red). */
  upIsGood: boolean;
  hint?: string;
}

const TILES: TileDef[] = [
  { key: 'dau', label: 'DAU', format: (v) => fmtNum(v), upIsGood: true },
  { key: 'newUsers', label: 'New users', format: (v) => fmtNum(v), upIsGood: true },
  {
    key: 'd1Retention',
    label: 'D1 retention',
    format: fmtPct,
    upIsGood: true,
    hint: "Share of an install day's new players who came back the next day.",
  },
  { key: 'minutesPerDau', label: 'Minutes / DAU', format: (v) => fmtNum(v, 1), upIsGood: true },
  { key: 'payers', label: 'Payers', format: (v) => fmtNum(v), upIsGood: true },
  {
    key: 'revenueUsd',
    label: 'Revenue (USD)',
    format: (v) => fmtUsd(v),
    upIsGood: true,
    hint: 'In-app purchases (gross, as GA4 reports them) plus ad revenue.',
  },
  { key: 'arpdauUsd', label: 'ARPDAU', format: (v) => fmtUsd(v, 4), upIsGood: true },
  {
    key: 'adImpressionsPerDau',
    label: 'Ad impressions / DAU',
    format: (v) => fmtNum(v, 2),
    upIsGood: true,
    hint: 'Not fill rate: the game logs ad requests for one ad flow only.',
  },
  {
    key: 'exceptionsPerDau',
    label: 'Exceptions / DAU',
    format: (v) => fmtNum(v, 4),
    upIsGood: false,
    hint: 'app_exception events per DAU. Crashlytics crashes are not in BigQuery.',
  },
];

const GOOD = '#389e0d';
const BAD = '#cf1322';

export interface BqKpiTilesProps {
  kpis: BqKpiDay[];
  dataThrough: string | null;
}

/** Latest day of each KPI against the average of the 7 days before it. */
export const BqKpiTiles: React.FC<BqKpiTilesProps> = ({ kpis, dataThrough }) => (
  <Row gutter={[12, 12]}>
    {TILES.map((t) => {
      const { latest, latestDate, avg7, changePct } = latestVsAverage(kpis, t.key);
      const older = latestDate !== null && dataThrough !== null && latestDate < dataThrough;
      const color =
        changePct === null || changePct === 0
          ? undefined
          : changePct > 0 === t.upIsGood
            ? GOOD
            : BAD;
      return (
        <Col key={t.key} xs={12} sm={8} lg={6} xl={4}>
          <div data-tile={t.key}>
            <Card size="small" style={{ height: '100%' }}>
              <Tooltip title={t.hint}>
                <Text type="secondary">{t.label}</Text>
              </Tooltip>
              <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.4 }}>
                {t.format(latest)}
              </div>
              <div style={{ fontSize: 12 }}>
                {changePct === null ? (
                  <Text type="secondary">
                    {latest === null ? 'not known yet' : 'no 7-day average yet'}
                  </Text>
                ) : (
                  <Tooltip title={`7-day average: ${t.format(avg7)}`}>
                    <span style={{ color }}>
                      {changePct > 0 ? '▲' : changePct < 0 ? '▼' : '•'}{' '}
                      {Math.abs(changePct).toFixed(0)}%
                    </span>{' '}
                    <Text type="secondary">vs 7-day avg</Text>
                  </Tooltip>
                )}
              </div>
              {older && latestDate && (
                <Text type="secondary" style={{ fontSize: 11 }}>
                  as of {dayLabel(latestDate)}
                </Text>
              )}
            </Card>
          </div>
        </Col>
      );
    })}
  </Row>
);
